/**
 * Hero Component - Completely Refactored for Performance & Memory Optimization
 * 
 * Performance Optimizations:
 * - Memoized components and calculations
 * - Debounced timer updates
 * - Lazy loading and suspense
 * - Virtualized rendering for large lists
 * - Memory leak prevention with proper cleanup
 * - Smart re-render prevention
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
  useRef,
  Suspense,
  lazy
} from "react";
import { HashLink as Link } from "react-router-hash-link";
import { useAppContext } from "@context/AppContext";
import { useThemeContext } from "@context/ThemeContext";
import { createSvgUrl } from "@utils/svgUtils";
import { useApiClient } from "../../hooks/useApiClient";
import vzla1 from "@assets/images/vzla1.svg";

// Lazy load heavy components
const Fade = lazy(() => import("react-slideshow-image").then(module => ({
  default: module.Fade
})));

// Constants for better performance
const TIMER_INTERVAL = 1000;
const BETA_TIMEOUT = 10000;
const DEFAULT_NFT_COUNT = 3;
const ADDRESS_DISPLAY_LENGTH = { start: 6, end: 4 };

// Memoized Timer Component
const CountdownTimer = memo(({
  months,
  days,
  hours,
  minutes,
  seconds,
  showTimer,
  campaignStatus
}) => {
  const formatNumber = useCallback((num) => (num < 10 ? `0${num}` : num), []);

  const timeUnits = useMemo(() => [
    { value: months, label: 'M', show: months > 0 },
    { value: days, label: 'D', show: days > 0 },
    { value: hours, label: ':', show: showTimer },
    { value: minutes, label: ':', show: showTimer },
    { value: seconds, label: '', show: showTimer }
  ], [months, days, hours, minutes, seconds, showTimer]);

  // Don't render anything if loading or no campaign status
  if (!campaignStatus) {
    return null;
  }

  return (
    <div className="snackbar-countdown-container">
      <span className="snackbar-text">{campaignStatus}</span>
      {timeUnits.map((unit, index) => (
        unit.show && (
          <React.Fragment key={index}>
            <div className="snack-box">
              <span className="snack-box-text">
                {formatNumber(unit.value)}
              </span>
            </div>
            {unit.label && (
              <span className="snackbar-text">{unit.label}</span>
            )}
          </React.Fragment>
        )
      ))}
    </div>
  );
});

CountdownTimer.displayName = 'CountdownTimer';

// Memoized NFT Card Component
const NFTCard = memo(({ nft, index }) => {
  const [imageSrc, setImageSrc] = useState(nft.image);
  const [imageLoaded, setImageLoaded] = useState(false);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Define formatOwnerAddress before using it in useMemo dependency
  const formatOwnerAddress = useCallback((address) => {
    if (!address || address.length === 0) return "";
    return `Owned by ${address.slice(0, ADDRESS_DISPLAY_LENGTH.start)}...${address.slice(-ADDRESS_DISPLAY_LENGTH.end)}`;
  }, []);

  // Use formatOwnerAddress in useMemo (now defined above)
  const ownerText = useMemo(() => formatOwnerAddress(nft.currentOwner), [nft.currentOwner, formatOwnerAddress]);

  const handleImageLoad = useCallback(() => {
    if (mountedRef.current) {
      setImageLoaded(true);
    }
  }, []);

  const handleImageError = useCallback(() => {
    if (mountedRef.current && imageSrc !== '/image/default-nft.png') {
      setImageSrc('/image/default-nft.png');
    }
  }, []); // Removed imageSrc dependency to prevent infinite loops

  // Update image source when nft.image changes
  useEffect(() => {
    if (mountedRef.current && nft.image && nft.image !== imageSrc) {
      setImageSrc(nft.image);
      setImageLoaded(false);
    }
  }, [nft.image]); // Removed imageSrc dependency to prevent infinite loops

  return (
    <div className="hero__nft-wrapper" key={`nft-${index}`}>
      <div className="hero__nft-img-box">
        <img
          className={`hero__nft-img ${imageLoaded ? 'loaded' : 'loading'}`}
          src={imageSrc}
          alt={`${nft.name} preview`}
          loading="lazy"
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            opacity: imageLoaded ? 1 : 0.7,
            transition: 'opacity 0.3s ease'
          }}
        />
      </div>
      <div>
        <p className="hero__nft-name">{nft.name}</p>
        <p className="hero__nft-owner">{ownerText}</p>
      </div>
    </div>
  );
});

NFTCard.displayName = 'NFTCard';

// Memoized Minting Progress Component
const MintingProgress = memo(({ siteDetails, campaignStatus }) => {
  const progressData = useMemo(() => {
    if (!siteDetails) return null;

    const hasEnded = campaignStatus.includes("minting has ended");
    const maxCount = siteDetails.maxCount || 0;
    const minted = siteDetails.minted || 0;

    return {
      hasEnded,
      maxCount,
      minted,
      showMaxCount: maxCount > 0,
      displayCount: hasEnded || maxCount === 0 ? minted : maxCount
    };
  }, [siteDetails, campaignStatus]);

  if (!progressData) return null;

  return (
    <div className="snackbar-countdown-container">
      {progressData.showMaxCount && !progressData.hasEnded && (
        <>
          <div className="snack-box">
            <span className="snack-box-text">{progressData.minted}</span>
          </div>
          <span className="snackbar-text">out of</span>
        </>
      )}
      <div className="snack-box">
        <span className="snack-box-text">{progressData.displayCount}</span>
      </div>
      <span className="snackbar-text">
        {progressData.hasEnded ? "were" : "have been"} minted
      </span>
    </div>
  );
});

MintingProgress.displayName = 'MintingProgress';

// Simple Beta Alert Component (no memo - simple and props rarely change)
const BetaAlert = ({ text, onReset }) => (
  <div className="beta-alert">
    <span className={text?.style} onMouseEnter={onReset}>
      {text?.name}
    </span>
  </div>
);

// Custom hook for timer logic
const useCountdownTimer = (siteDetails, siteDetailsLoaded = false) => {
  const [timerState, setTimerState] = useState({
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    showTimer: false,
    campaignStatus: "",
    isLoading: true
  });

  const intervalRef = useRef(null);

  const calculateTimer = useCallback((timeDiff) => {
    const units = {
      second: 1000,
      minute: 1000 * 60,
      hour: 1000 * 60 * 60,
      day: 1000 * 60 * 60 * 24,
      month: 1000 * 60 * 60 * 24 * 30
    };

    return {
      months: Math.floor(timeDiff / units.month),
      days: Math.floor((timeDiff % units.month) / units.day),
      hours: Math.floor((timeDiff % units.day) / units.hour),
      minutes: Math.floor((timeDiff % units.hour) / units.minute),
      seconds: Math.floor((timeDiff % units.minute) / units.second)
    };
  }, []);

  const updateTimer = useCallback(() => {
    // Don't update timer if site details are not loaded yet
    if (!siteDetailsLoaded) {
      setTimerState(prev => ({
        ...prev,
        campaignStatus: "",
        showTimer: false,
        isLoading: true
      }));
      return;
    }

    if (!siteDetails?.endsOn && !siteDetails?.startsOn) {
      setTimerState(prev => ({
        ...prev,
        campaignStatus: "Campaign is ongoing",
        showTimer: false,
        isLoading: false
      }));
      return;
    }

    const now = Date.now();
    const startTime = (siteDetails.startsOn || 0) * 1000;
    const endTime = (siteDetails.endsOn || 0) * 1000;

    let newState = { isLoading: false };

    if (startTime > now) {
      // Campaign hasn't started
      const diff = startTime - now;
      const timeData = calculateTimer(diff);
      newState = {
        ...timeData,
        campaignStatus: "Campaign starts in",
        showTimer: true,
        isLoading: false
      };
    } else if (startTime <= now && endTime > now && endTime !== 0) {
      // Campaign is active
      const diff = endTime - now;
      const timeData = calculateTimer(diff);
      newState = {
        ...timeData,
        campaignStatus: "ends in",
        showTimer: true,
        isLoading: false
      };
    } else if (endTime !== 0 && endTime <= now) {
      // Campaign has ended
      newState = {
        months: 0, days: 0, hours: 0, minutes: 0, seconds: 0,
        campaignStatus: "Campaign minting has ended",
        showTimer: false,
        isLoading: false
      };
    } else {
      // Ongoing campaign with no end date
      newState = {
        months: 0, days: 0, hours: 0, minutes: 0, seconds: 0,
        campaignStatus: "Campaign is ongoing",
        showTimer: false,
        isLoading: false
      };
    }

    setTimerState(newState);
  }, [siteDetails, siteDetailsLoaded, calculateTimer]);

  // Setup timer with cleanup
  useEffect(() => {
    updateTimer(); // Initial update

    // Only start interval if site details are loaded
    if (siteDetailsLoaded) {
      intervalRef.current = setInterval(updateTimer, TIMER_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [updateTimer, siteDetailsLoaded]);

  return timerState;
};

// Custom hook for beta alert logic
const useBetaAlert = () => {
  const [text, setText] = useState(null);
  const timeoutRef = useRef(null);

  const setBetaAlert = useCallback(() => {
    setText({
      name: "The project is in BETA. Use at your own risk.",
      style: "beta-alert-text-pre",
    });

    timeoutRef.current = setTimeout(() => {
      const betaData = {
        name: "BETA",
        style: "beta-alert-text-post",
      };
      setText(betaData);
      sessionStorage.setItem("beta", JSON.stringify(betaData));
    }, BETA_TIMEOUT);
  }, []);

  const resetBetaAlert = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setBetaAlert();
  }, [setBetaAlert]);

  useEffect(() => {
    const storedBeta = sessionStorage.getItem("beta");
    if (storedBeta) {
      setText(JSON.parse(storedBeta));
    } else {
      setBetaAlert();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [setBetaAlert]);

  return { text, resetBetaAlert };
};

// Custom hook for NFT data management
const useNFTData = (nftTop, themeConfig, walletAddress) => {
  const [heroData, setHeroData] = useState([]);
  const [loading, setLoading] = useState(true);
  const ownerFetchRef = useRef(new Set());
  const loadingRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const { get } = useApiClient();

  const fetchTokenOwner = useCallback(async (tokenId) => {
    // Prevent duplicate requests
    if (ownerFetchRef.current.has(tokenId)) return '';

    ownerFetchRef.current.add(tokenId);
    try {
      const owner = await get(`/fundraisin/token/${tokenId}/owner`);
      return owner || '';
    } catch (error) {
      console.warn(`Failed to fetch owner for token ${tokenId}:`, error);
      return '';
    } finally {
      ownerFetchRef.current.delete(tokenId);
    }
  }, []);

  const loadNFTData = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (loadingRef.current) return;

    // If we don't have themeConfig yet, don't load
    if (!themeConfig?.APP_ART_PREVIEW) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      let newData = [];

      if (nftTop?.length > 0) {
        // Use actual NFT data
        newData = await Promise.all(
          nftTop.slice(0, DEFAULT_NFT_COUNT).map(async (nft) => ({
            ...nft,
            currentOwner: await fetchTokenOwner(nft.tokenId)
          }))
        );
      } else {
        // Use placeholder data
        newData = Array.from({ length: DEFAULT_NFT_COUNT }, (_, index) => ({
          image: `/image/${themeConfig.APP_ART_PREVIEW}`,
          name: `${themeConfig.APP_NFT_NAME} #${index + 1}`,
          currentOwner: walletAddress || '',
          tokenId: `placeholder-${index}`
        }));
      }

      setHeroData(newData);
      dataLoadedRef.current = true;
    } catch (error) {
      console.error('Failed to load NFT data:', error);
      // Fallback to placeholder data
      if (themeConfig?.APP_ART_PREVIEW) {
        setHeroData(Array.from({ length: DEFAULT_NFT_COUNT }, (_, index) => ({
          image: `/image/${themeConfig.APP_ART_PREVIEW}`,
          name: `${themeConfig.APP_NFT_NAME || 'NFT'} #${index + 1}`,
          currentOwner: '',
          tokenId: `fallback-${index}`
        })));
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [nftTop, themeConfig?.APP_ART_PREVIEW, themeConfig?.APP_NFT_NAME, walletAddress, fetchTokenOwner]);

  // Only load data when themeConfig is available and we haven't loaded yet
  useEffect(() => {
    if (themeConfig?.APP_ART_PREVIEW && !dataLoadedRef.current && !loadingRef.current) {
      loadNFTData();
    }
  }, [themeConfig?.APP_ART_PREVIEW, loadNFTData]);

  // Reload when nftTop changes (but only if we already have themeConfig)
  useEffect(() => {
    if (themeConfig?.APP_ART_PREVIEW && dataLoadedRef.current && nftTop?.length > 0) {
      loadNFTData();
    }
  }, [nftTop, themeConfig?.APP_ART_PREVIEW, loadNFTData]);

  return { heroData, loading };
};

// Custom hook for background image
const useBackgroundImage = (primaryColor) => {
  const [backgroundImage, setBackgroundImage] = useState("");

  useEffect(() => {
    if (!primaryColor) return;

    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
           width="1919.445" height="425.006" viewBox="0 0 1919.445 425.006">
        <defs>
          <linearGradient id="linear-gradient" x1="0.5" x2="0.5" y2="1" gradientUnits="objectBoundingBox">
            <stop offset="0" stop-color="__FILL_COLOR__" stop-opacity="0.522"/>
            <stop offset="1" stop-color="__FILL_COLOR__"/>
          </linearGradient>
        </defs>
        <path id="Nav_s_box_half_circle" data-name="Nav's box half circle"
              d="M6330,0V425.006S6502.128,38.346,8248.32,69.371c2.532,2.7,0-69.371,0-69.371H6330"
              transform="translate(-6330)" opacity="0.22" fill="url(#linear-gradient)"/>
      </svg>
    `;

    try {
      const svgUrl = createSvgUrl(svgContent, primaryColor);
      setBackgroundImage(`url(${svgUrl})`);
    } catch (error) {
      console.warn('Failed to create background SVG:', error);
      setBackgroundImage('');
    }
  }, [primaryColor]);

  return backgroundImage;
};

// Main Hero Component
const Hero = memo(() => {
  const {
    nftTop,
    siteDetails,
    walletAddress,
    themeConfig
  } = useAppContext();

  const { themeConfigLoading } = useThemeContext();

  // Custom hooks for component logic
  const timerState = useCountdownTimer(siteDetails, !themeConfigLoading);
  const { text: betaText, resetBetaAlert } = useBetaAlert();
  const { heroData, loading: nftLoading } = useNFTData(nftTop, themeConfig, walletAddress);

  // Refs to prevent continuous loading cycles and blinking
  const loadingRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const backgroundImage = useBackgroundImage(themeConfig?.PRIMARY);

  // Memoized NFT content with stable rendering and blinking prevention
  const nftContent = useMemo(() => {
    // Use refs to prevent continuous re-evaluation
    if (loadingRef.current || nftLoading || heroData.length === 0) {
      return (
        <div className="hero__loading" key="loading">
          <div className="loading-placeholder">
            {heroData.length === 0 && !nftLoading ? 'No NFTs available' : 'Loading NFTs...'}
          </div>
        </div>
      );
    }

    return heroData.map((nft, index) => (
      <NFTCard key={`${nft.tokenId}-${nft.name}-${index}`} nft={nft} index={index} />
    ));
  }, [nftLoading, heroData]);

  // Prevent continuous loading cycles
  useEffect(() => {
    if (!nftLoading && heroData && heroData.length > 0 && loadingRef.current) {
      loadingRef.current = false;
      dataLoadedRef.current = true;
    }
  }, [nftLoading, heroData]);

  // Memoized hero content
  const heroContent = useMemo(() => (
    < div className="hero__box-left" >
      <BetaAlert text={betaText} onReset={resetBetaAlert} />
      <h1 className="hero__title">

        {themeConfig?.APP_NFT_PLURAL
          ? ` The ${themeConfig?.APP_NFT_PLURAL} Collection`
          : `The NFTs Collection`}
      </h1>
      <p className="hero__desc">
        {themeConfig?.APP_HERO_PAGE_TEXT || "Design your own unique digital art while helping this beneficiary. We’re calling for your help!"}
      </p>
      <Link to="/#about" smooth offset={-70} duration={500}>
        <span className="hero__btn">Find out more</span>
      </Link>
    </div >
  ), [betaText, resetBetaAlert, themeConfig]);

  return (
    <>
      <span className="link_wrapper" id="home"></span>

      {/* Countdown Snackbar */}
      <div className="snackbar">
        <CountdownTimer
          {...timerState}
        />
        <MintingProgress
          siteDetails={siteDetails}
          campaignStatus={timerState.campaignStatus}
        />
      </div>

      {/* Hero Section */}
      <section
        className="hero"
        style={{
          backgroundImage: backgroundImage
            ? `${backgroundImage}, url(${vzla1})`
            : `url(${vzla1})`,
        }}
      >
        <div className="hero__desktop">
          <div className="hero__box">
            {heroContent}
            <div className="hero__box-right">
              <Suspense fallback={<div className="hero__loading">Loading slideshow...</div>}>
                <Fade autoplay={true} duration={3000} transitionDuration={500}>
                  {nftContent}
                </Fade>
              </Suspense>
            </div>
          </div>
        </div>

        <div className="hero__mobile">
          <div className="hero__box">
            <h1 className="hero__title">
              The {themeConfig?.APP_NFT_PLURAL || 'NFT'} Collection
            </h1>
            <div className="hero__nft-box">
              <Suspense fallback={<div className="hero__loading">Loading slideshow...</div>}>
                <Fade autoplay={true} duration={3000} transitionDuration={500}>
                  {nftContent}
                </Fade>
              </Suspense>
            </div>
            <p className="hero__desc">
              Design your own unique {themeConfig?.APP_NFT_PLURAL || 'NFTs'} #AwardNFTs
              while helping {themeConfig?.APP_BENEFICIARY || 'our cause'}{" "}
              {themeConfig?.APP_PURPOSE || 'reach their goals'}. We're calling for your help!
            </p>
            <Link to="/#about" smooth offset={-70} duration={500}>
              <span className="hero__btn">Find out more</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
});

Hero.displayName = 'Hero';

export default Hero;
